import React, { useState, useEffect, useCallback } from "react";
import "./InterviewCalendar.css";

const RAILWAY = process.env.REACT_APP_API_BASE || "";
const TZ = "America/New_York";

const dayLabel = (iso) =>
  new Intl.DateTimeFormat("en-US", { timeZone: TZ, weekday: "long", month: "long", day: "numeric" }).format(new Date(iso));
const timeLabel = (iso) =>
  new Intl.DateTimeFormat("en-US", { timeZone: TZ, hour: "numeric", minute: "2-digit", hour12: true }).format(new Date(iso));
const dateKey = (iso) =>
  new Intl.DateTimeFormat("en-CA", { timeZone: TZ, year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date(iso));

export default function InterviewCalendar() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [copied, setCopied]     = useState(false);

  const publicLink = `${RAILWAY}/interview`;

  const load = useCallback(() => {
    setLoading(true); setError(null);
    fetch(`${RAILWAY}/api/interview/bookings`)
      .then(r => r.json())
      .then(d => setBookings(d.bookings || []))
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const copyLink = () => {
    navigator.clipboard?.writeText(publicLink).then(() => {
      setCopied(true); setTimeout(() => setCopied(false), 2500);
    }).catch(() => {});
  };

  const cancel = (slotId, name) => {
    if (!window.confirm(`Remove the interview booking for ${name || "this applicant"}?`)) return;
    fetch(`${RAILWAY}/api/interview/cancel`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ slotId }),
    }).then(r => r.json()).then(() => load()).catch(() => {});
  };

  // Group by ET calendar day, upcoming first (backend already sorts by time).
  const now = Date.now();
  const upcoming = bookings.filter(b => new Date(b.startISO).getTime() >= now - 3600000);
  const groups = [];
  upcoming.forEach(b => {
    const k = dateKey(b.startISO);
    let g = groups.find(x => x.key === k);
    if (!g) { g = { key: k, label: dayLabel(b.startISO), rows: [] }; groups.push(g); }
    g.rows.push(b);
  });

  return (
    <div className="ivc">
      <div className="ivc-head">
        <div>
          <h2 className="ivc-title">📅 Interview Calendar</h2>
          <p className="ivc-sub">Applicants who have self-scheduled. Times are Eastern (ET).</p>
        </div>
        <div className="ivc-actions">
          <button className="ivc-btn" onClick={copyLink} title={publicLink}>
            {copied ? "✓ Link copied" : "🔗 Copy scheduling link"}
          </button>
          <button className="ivc-btn ghost" onClick={load} disabled={loading}>
            {loading ? "…" : "↻ Refresh"}
          </button>
        </div>
      </div>

      <div className="ivc-linkbar">
        <span className="ivc-linklabel">Applicant link:</span>
        <code className="ivc-link">{publicLink}</code>
      </div>

      {error && <div className="ivc-error">⚠ {error}</div>}

      {!loading && upcoming.length === 0 && !error && (
        <div className="ivc-empty">
          <div style={{ fontSize: 30, marginBottom: 8 }}>🗓️</div>
          <p>No upcoming interviews scheduled yet.</p>
          <p className="ivc-empty-sub">Send the applicant link above to eligible candidates to start filling the calendar.</p>
        </div>
      )}

      <div className="ivc-list">
        {groups.map(g => (
          <div key={g.key} className="ivc-day">
            <div className="ivc-day-label">{g.label}</div>
            {g.rows.map(b => (
              <div key={b.slotId} className="ivc-row">
                <div className="ivc-time">{timeLabel(b.startISO)}</div>
                <div className="ivc-who">
                  <div className="ivc-name">{b.name || "(name unavailable)"}</div>
                  <div className="ivc-email">{b.email}</div>
                </div>
                <div className="ivc-src">
                  {b.source === "teambridge"
                    ? <span className="ivc-badge">Teambridge</span>
                    : <button className="ivc-cancel" onClick={() => cancel(b.slotId, b.name)}>Cancel</button>}
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
